<template>
	<div
		class="flex mt-4 items-center gap-2"
		:class="[
      'mb-4',
      'text-sm',
      ...classes,
      'dark:bg-gray-800'
    ]"
		role="alert"
	>
		<AppIcon
			v-if="type in iconMap"
			:icon="[inline ? 'fa' : 'fal', iconMap[type]]"
			v-bind="{
        size: inline ? null : '2x',
      }"
			fixed-width
			:class="iconColor"
		/>
		<div class="self-center inline-block pl-1">
			<slot/>
		</div>
		<template v-if="hasDetails">
			<button type="button"
			        class="ms-auto -mx-1.5 -my-1.5 bg-opacity-10 bg-black rounded-lg focus:ring-2 focus:ring-black focus:ring-opacity-40 p-1.5 hover:bg-opacity-15 inline-flex items-center justify-center h-8 w-8"
			        @click="onReadDetails" aria-label="OpenDetails">
				<AppIcon :icon="hasDetailsIcon"></AppIcon>
			</button>
		</template>
		<template v-if="dismissable">
			<button type="button"
			        class="-mx-1.5 -my-1.5 bg-opacity-10 bg-black rounded-lg focus:ring-2 focus:ring-black focus:ring-opacity-40 p-1.5 hover:bg-opacity-15 inline-flex items-center justify-center h-8 w-8 ml-1"
			        :class="{ 'ms-auto': !hasDetails}"
			        @click="onDismiss" aria-label="Markeren als gelezen">
				<AppIcon :icon="dismissableIcon"></AppIcon>
			</button>
		</template>
	</div>
</template>

<script setup lang="ts">
const props = withDefaults(
	defineProps<{
		type: "info" | "success" | "warning" | "error" | "neutral" | "read";
		inline?: boolean;
		banner?: boolean;
		dismissable?: boolean;
		dismissableIcon?: string;
		hasDetails?: boolean;
		hasDetailsIcon?: string;
		wasRead?: boolean;
	}>(),
	{
		inline: false,
		banner: false,
		dismissable: false,
		dismissableIcon: "close",
		hasDetails: false,
		hasDetailsIcon: "close",
		wasRead: false,
	},
);

const emit = defineEmits(["dismiss", "read-details"]);

const iconMap = {
	info: "info",
	success: "circle-check",
	warning: "lightbulb-exclamation",
	error: "triangle-exclamation",
	read: "info",
};

const colorMap = {
	info: "blue",
	success: "green",
	warning: "amber",
	error: "red",
	neutral: "gray",
	read: "gray",
};

const classes = computed(() => {
	if (props.inline) {
		return [`text-${colorMap[props.type]}-700`, "font-bold", `dark:text-${colorMap[props.type]}-300`];
	}

	let classes = [
		"p-4",
		"border",
		"border-solid",
		`border-${colorMap[props.type]}-800`,
		`text-${colorMap[props.type]}-900`,
		`bg-${colorMap[props.type]}-100`,
		`dark:text-${colorMap[props.type]}-300`,
	];

	if (props.wasRead) {
		classes = [
			"p-4",
			"border",
			"border-solid",
			`border-${colorMap[props.type]}-800`,
			"border-opacity-60",
			`text-${colorMap[props.type]}-900`,
			"bg-gray-100",
			"dark:text-gray-300",
		];
	}

	if (props.banner) {
		classes.push("border-l-0", "border-r-0");
	}

	if (!props.inline) {
		classes.push("w-full");
	}

	if (!props.banner && !props.inline) {
		classes.push("rounded-lg");
	}

	return classes;
});

const iconColor = computed(() => {
	return [`text-${colorMap[props.type]}-900`];
});

function onDismiss() {
	emit("dismiss");
}

function onReadDetails() {
	emit("read-details");
}
</script>
